import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, TextField, Pagination,
  Stack, Tooltip, Chip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { deletePaymentMethod, getAllPaymentMethods } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormPaymentMethod from './FormPaymentMethod';
import useFeedbackAlert from './common/useFeedbackAlert';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { sxLinhaInativa } from './common/StatusAtivo';

export default function PaymentMethodList() {
  const { showSuccess, showApiError, askConfirm, setConfirmLoading, FeedbackUi } = useFeedbackAlert();
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina] = useState(20);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');
  const [form, setForm] = useState({ open: false, id: null });

  const carregar = useCallback(async () => {
    try {
      const { data } = await getAllPaymentMethods();
      setItems(Array.isArray(data) ? data : []);
      setPagina(1);
    } catch (err) {
      showApiError(err, 'Erro ao carregar formas de pagamento.');
    }
  }, [showApiError]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    showSuccess(msg);
    carregar();
  };

  const filtrados = useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return items;
    return items.filter((f) =>
      (f.descricao || '').toLowerCase().includes(t) ||
      String(f.referenciaSat ?? '').includes(t)
    );
  }, [items, termo]);

  const totalItens = filtrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));
  const paymentAccessors = useMemo(() => ({
    tPag: (f) => f.referenciaSat,
    flags: (f) => [
      f.pix && 'pix',
      f.pixIntegrado && 'pixInt',
      f.tef && 'tef',
      f.troco && 'troco',
      f.convenio && 'convenio',
      f.troca && 'troca',
    ].filter(Boolean).join(','),
    status: (f) => (f.ativo ? 1 : 0),
  }), []);
  const { sorted: ordenados, orderBy, order, requestSort } = useClientSort(
    filtrados,
    'descricao',
    'asc',
    paymentAccessors,
  );
  const paginaOrdenada = ordenados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);
  const paginaAtual = paginaOrdenada;

  const solicitarExclusao = async (id) => {
    const item = items.find((f) => f.id === id);
    const ok = await askConfirm({
      title: 'Excluir forma de pagamento',
      message: item?.descricao
        ? `Deseja excluir "${item.descricao}"?\nEsta ação não pode ser desfeita.`
        : 'Deseja excluir esta forma de pagamento?',
      confirmLabel: 'Excluir',
      confirmColor: 'error',
    });
    if (!ok) return;

    setConfirmLoading(true);
    try {
      await deletePaymentMethod(id);
      showSuccess('Forma de pagamento excluída.');
      await carregar();
    } catch (err) {
      showApiError(err, 'Erro ao excluir forma de pagamento.');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Formas de pagamento"
        subtitle="Meios de pagamento disponíveis no PDV e nos relatórios."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
            Nova forma
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por descrição ou tPag..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setTermo(searchText)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: !!searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}><CloseIcon fontSize="small" /></IconButton>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />
        <Button variant="outlined" onClick={() => setTermo(searchText)}>Buscar</Button>
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="ID" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="tPag" label="tPag" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="flags" label="Flags" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginaAtual.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma forma de pagamento encontrada
                </TableCell>
              </TableRow>
            ) : paginaOrdenada.map((f) => (
              <TableRow key={f.id} hover sx={{ cursor: 'pointer', ...sxLinhaInativa(f.ativo) }} onClick={() => abrirForm(f.id)}>
                <TableCell data-label="ID">{f.id}</TableCell>
                <TableCell data-label="Descrição">{f.descricao}</TableCell>
                <TableCell data-label="tPag">{String(f.referenciaSat).padStart(2, '0')}</TableCell>
                <TableCell data-label="Flags">
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {f.pix && <Chip size="small" label="PIX" />}
                    {f.pixIntegrado && <Chip size="small" label="PIX Int." color="primary" />}
                    {f.tef && <Chip size="small" label="TEF" color="secondary" />}
                    {f.troco && <Chip size="small" label="Troco" variant="outlined" />}
                    {f.convenio && <Chip size="small" label="Convênio" variant="outlined" />}
                    {f.troca && <Chip size="small" label="Troca" variant="outlined" />}
                  </Stack>
                </TableCell>
                <TableCell data-label="Status">
                  <Chip size="small" label={f.ativo ? 'Ativo' : 'Inativo'} color={f.ativo ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirForm(f.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => solicitarExclusao(f.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary" component="span">
          Exibindo {paginaAtual.length} de {totalItens} (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar forma de pagamento' : 'Nova forma de pagamento'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="md"
      >
        <FormPaymentMethod
          key={form.id || 'novo'}
          embedded
          paymentMethodId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <FeedbackUi />
    </Box>
  );
}
